import React, { useEffect, useState } from 'react';
import { Card, Badge, EmptyState, ErrorNote } from '../ui';

interface PublishedRow {
  id: string;
  fbObjectId: string;
  fbObjectType: string;
  permalinkUrl: string | null;
  publishedAt: string;
  processingStatus: string | null;
  title: string | null;
  contentId: string;
  contentType: string;
  pageLabel: string;
}

interface PageOption { id: string; internalLabel: string }

export function PublishedScreen({ pageId }: { pageId?: string } = {}) {
  const [rows, setRows] = useState<PublishedRow[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [localFilter, setLocalFilter] = useState('');
  const pageFilter = pageId ?? localFilter;
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setPages((await window.pagepilot.pages.list()) as PageOption[]);
      setRows((await window.pagepilot.content.published(pageFilter || undefined)) as PublishedRow[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 20_000);
    return () => clearInterval(t);
  }, [pageFilter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Published Content</h1>
        {!pageId && (
          <select value={localFilter} onChange={(e) => setLocalFilter(e.target.value)} className="ml-auto rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm">
            <option value="">All Pages</option>
            {pages.map((p) => <option key={p.id} value={p.id}>{p.internalLabel}</option>)}
          </select>
        )}
      </div>
      {error && <ErrorNote message={error} />}

      <Card>
        {rows.length === 0 ? (
          <EmptyState title="Nothing published yet" hint="Once content is published to Facebook, its object IDs and processing status are recorded here." />
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="text-neutral-500">
              <tr>
                <th className="pb-2 pr-3 font-medium">Published</th>
                <th className="pb-2 pr-3 font-medium">Type</th>
                <th className="pb-2 pr-3 font-medium">Title</th>
                <th className="pb-2 pr-3 font-medium">Page</th>
                <th className="pb-2 pr-3 font-medium">Facebook ID</th>
                <th className="pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-neutral-800/60">
                  <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums text-neutral-400">{new Date(r.publishedAt).toLocaleString()}</td>
                  <td className="py-1.5 pr-3">{r.contentType === 'REEL' ? '▣ Reel' : '◻ Image'}</td>
                  <td className="max-w-[280px] truncate py-1.5 pr-3 text-neutral-200">{r.title ?? 'Untitled'}</td>
                  <td className="py-1.5 pr-3 text-neutral-400">{r.pageLabel}</td>
                  <td className="py-1.5 pr-3 font-mono text-neutral-500">{r.fbObjectId}</td>
                  <td className="py-1.5">
                    <Badge status={r.processingStatus === 'error' ? 'MISCONFIGURED' : r.processingStatus === 'processing' ? 'BUSY' : 'HEALTHY'} label={r.processingStatus ?? 'published'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
